<template>
    <div>
        <v-autocomplete
            v-model="selectedUser"
            :items="searchResults"
            :loading="searching"
            :search-input.sync="addTerm"
            item-text="name"
            item-value="id"
            label="Adicionar usuário ao perfil"
            placeholder="Digite o nome ou e-mail para buscar…"
            prepend-inner-icon="mdi-account-plus"
            return-object
            outlined
            dense
            clearable
            hide-no-data
            hide-details
            no-filter
            class="mb-4"
            @change="addUser"
        >
            <template v-slot:item="{ item }">
                <v-list-item-avatar>
                    <EmcBaseAvatar :text="initials(item.name)" :src="item.url_avatar" :size="36" color="primary" />
                </v-list-item-avatar>
                <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                </v-list-item-content>
            </template>
        </v-autocomplete>

        <div class="d-flex align-center mb-2">
            <v-icon small color="grey darken-1" class="mr-2">mdi-account-group</v-icon>
            <span class="subtitle-2 grey--text text--darken-2">Usuários vinculados</span>
            <v-spacer />
            <v-chip x-small label outlined color="primary">{{ total }}</v-chip>
        </div>

        <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Filtrar vinculados"
            dense
            outlined
            clearable
            hide-details
            class="mb-3"
            @input="onSearch"
        />

        <v-card outlined>
            <v-data-table
                :headers="headers"
                :items="users"
                :server-items-length="total"
                :options.sync="options"
                :loading="loading"
                :footer-props="{ 'items-per-page-options': [5, 10, 20, 50] }"
                dense
            >
                <template v-slot:item.name="{ item }">
                    <div class="d-flex align-center py-1">
                        <EmcBaseAvatar
                            :text="initials(item.name)"
                            :src="item.url_avatar"
                            :size="32"
                            color="primary"
                            class="mr-2"
                        />
                        {{ item.name }}
                    </div>
                </template>
                <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                icon
                                small
                                color="error"
                                v-bind="attrs"
                                v-on="on"
                                :loading="removingId === item.id"
                                @click="removeUser(item)"
                            >
                                <v-icon small>mdi-account-remove</v-icon>
                            </v-btn>
                        </template>
                        <span>Remover do perfil</span>
                    </v-tooltip>
                </template>
                <template v-slot:no-data>
                    <div class="grey--text py-4 caption">Nenhum usuário vinculado a este perfil.</div>
                </template>
            </v-data-table>
        </v-card>

        <v-alert dense text type="info" border="left" class="mt-3 mb-0">
            As alterações de usuários são aplicadas imediatamente — não dependem do botão Salvar.
        </v-alert>

        <emc-alert-snackbar :show="!!message.length" :message="message" :color="error ? 'error' : 'success'" />
    </div>
</template>

<script setup>

import { defineProps, ref, watch } from 'vue'
import { useStore } from '@/composables/useStore'

const store = useStore()

const props = defineProps({
    role: { type: Object, default: () => ({}) }
})

const headers = [
    { text: 'Usuário', value: 'name', sortable: false },
    { text: 'E-mail', value: 'email', sortable: false },
    { text: '', value: 'actions', sortable: false, align: 'end', width: 60 }
]

const users = ref([])
const total = ref(0)
const loading = ref(false)
const options = ref({ page: 1, itemsPerPage: 10 })

const search = ref('')
let searchTimer = null

const addTerm = ref('')
const searchResults = ref([])
const searching = ref(false)
const selectedUser = ref(null)
let addTimer = null

const removingId = ref(null)
const message = ref('')
const error = ref(false)

const initials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

const notify = (msg, isError = false) => {
    error.value = isError
    message.value = ''
    setTimeout(() => { message.value = msg }, 0)
}

const loadUsers = async () => {
    loading.value = true
    try {
        const res = await store.dispatch('role/ActionRoleUsers', {
            id: props.role.id,
            page: options.value.page,
            per_page: options.value.itemsPerPage,
            search: search.value || undefined
        })
        users.value = res.data || []
        total.value = (res.meta && res.meta.total) || 0
    } finally {
        loading.value = false
    }
}

// Paginação/itens por página (v-data-table emite update:options)
watch(options, loadUsers, { deep: true })

const onSearch = () => {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
        // novo objeto força o watch a recarregar já na primeira página
        options.value = { ...options.value, page: 1 }
    }, 350)
}

// Autocomplete de adição: busca em todos os usuários da instituição
watch(addTerm, (term) => {
    if (addTimer) clearTimeout(addTimer)

    if (!term || term.length < 2) {
        searchResults.value = []
        return
    }

    addTimer = setTimeout(async () => {
        searching.value = true
        try {
            const res = await store.dispatch('role/ActionSearchUsers', { search: term })
            const linkedIds = users.value.map((u) => u.id)
            searchResults.value = (res.data || []).filter((u) => !linkedIds.includes(u.id))
        } finally {
            searching.value = false
        }
    }, 350)
})

const addUser = async (user) => {
    if (!user || !user.id) return

    try {
        await store.dispatch('role/ActionAttachUserRole', { user_id: user.id, role_id: props.role.id })
        notify('Usuário vinculado ao perfil.')
        await loadUsers()
    } catch (e) {
        const msg = e && e.code === 422 ? 'Usuário já vinculado a este perfil.' : (e && e.message ? e.message : 'Não foi possível vincular o usuário.')
        notify(msg, true)
    } finally {
        resetAdd()
    }
}

const removeUser = async (user) => {
    removingId.value = user.id
    try {
        await store.dispatch('role/ActionRevokeUserRole', { user_id: user.id, role_id: props.role.id })
        notify('Usuário removido do perfil.')

        // se removeu o último item de uma página > 1, volta uma página
        if (users.value.length === 1 && options.value.page > 1) {
            options.value = { ...options.value, page: options.value.page - 1 }
        } else {
            await loadUsers()
        }
    } catch (e) {
        notify(e && e.message ? e.message : 'Não foi possível remover o usuário.', true)
    } finally {
        removingId.value = null
    }
}

const resetAdd = () => {
    selectedUser.value = null
    addTerm.value = ''
    searchResults.value = []
}

// O carregamento inicial é disparado pelo update:options que o v-data-table emite ao montar.

</script>
